import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { hasSecondaryPriceDisplay } from '@/domain/pricing/utils/pricing-utils';
import { PriceEditType } from '@/types';
import { PriceOverrideInfo } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceOverrideInfoDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/price-override-info';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface OrderEntryTableReadOnlyPriceCellProps {
  canSetManualOverrideExpiration?: boolean;
  priceDisplayString: string;
  priceOverrideInfo?: PriceOverrideInfo;
  showPriceOverrideInfo?: boolean;
  unitCount: number;
  unitPriceInfo?: string;
}

export const OrderEntryTableReadOnlyPriceCell: React.FC<
  OrderEntryTableReadOnlyPriceCellProps
> = ({
  canSetManualOverrideExpiration,
  priceDisplayString,
  priceOverrideInfo,
  showPriceOverrideInfo,
  unitCount,
  unitPriceInfo,
}) => {
  const [styles, theme] = useStyles();
  const showSecondaryPrice = hasSecondaryPriceDisplay(unitCount);
  const hasOverride =
    showPriceOverrideInfo && priceOverrideInfo?.activeOverride !== undefined;

  return (
    <View style={showPriceOverrideInfo ? styles.overrideCell : undefined}>
      {showSecondaryPrice && unitPriceInfo ? (
        <Typography
          color={theme.sk.color.text.secondary}
          numberOfLines={1}
          variant="paragraph-md"
        >
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {priceDisplayString}
          </Typography>
          <Typography textTransform="lowercase" variant="paragraph-sm">
            {` (${unitPriceInfo})`}
          </Typography>
        </Typography>
      ) : (
        <Typography
          color={theme.sk.color.text.primary}
          numberOfLines={1}
          variant="paragraph-md"
        >
          {priceDisplayString}
        </Typography>
      )}
      {hasOverride && priceOverrideInfo ? (
        <PriceOverrideInfoDisplay
          defaultValue={
            showSecondaryPrice && priceOverrideInfo.defaultUnitPriceDisplay
              ? `${priceOverrideInfo.defaultPackPriceDisplay} (${priceOverrideInfo.defaultUnitPriceDisplay})`
              : priceOverrideInfo.defaultPackPriceDisplay
          }
          editedByName={priceOverrideInfo.editedByName}
          expiryDateDisplay={priceOverrideInfo.expiryDateDisplay}
          isInline
          lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_price"
          showOverrideIndicator={
            priceOverrideInfo.activeOverride === PriceEditType.Price
          }
          showTemporaryIndicator={
            !!canSetManualOverrideExpiration &&
            priceOverrideInfo.isTemporaryOverride
          }
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  overrideCell: {
    minHeight: theme.sk.spacing.space3xlarge,
  },
}));
